import { useState, type FormEvent } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { money } from '@wyocspay/ui';
import type { MethodsView, PaymentView } from './api.js';
import type { Strings } from './i18n.js';
import { PAYMENT_ELEMENT_LAYOUT } from './stripe-appearance.js';
import { Panel, PAY_TOKENS } from './Shell.js';
import { ResultView, type ResultPhase } from './Result.js';
import { useSecondsLeft } from './useCountdown.js';

const { NAVY, INK, MUTED, FAINT, LINE } = PAY_TOKENS;

/**
 * The checkout, as steps 1 (Payment method) and 2 (Confirmation) of the wizard.
 *
 * Step 1 shows the payment request and the embedded Stripe Payment Element (the
 * card fields live inside Stripe's iframe - PCI SAQ A, nothing enters this DOM).
 * Step 2 is a review: the order summary, the acceptance, and the Pay button.
 * The Payment Element stays mounted the whole time (hidden on step 2) so the
 * confirmation still has it to submit. `redirect: 'if_required'` keeps card
 * payments on-page; the Result screen then asks the server what really happened.
 */
export function CheckoutForm({
  t,
  locale,
  view,
  methods,
  step,
  expiresAt,
  onStep,
  onSubmitted,
  onOpenLegal,
}: {
  t: Strings;
  locale: string;
  view: PaymentView;
  methods: MethodsView | null;
  step: 1 | 2 | 3;
  /** The session expiry (ISO); the Pay action locks once it passes. */
  expiresAt: string | null;
  onStep: (n: 1 | 2 | 3) => void;
  onSubmitted: () => void;
  onOpenLegal: (doc: 'terms' | 'privacy') => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [phase, setPhase] = useState<ResultPhase | null>(null);
  const [decline, setDecline] = useState<{ msg: string; code: string | null } | null>(null);
  const { payment, merchant } = view;
  const x = t as unknown as Record<string, string>;
  const left = useSecondsLeft(expiresAt);
  const expired = left !== null && left <= 0;

  async function pay(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!stripe || !elements || submitting) return;
    if (expired) { setMessage(x.sessionExpiredNote ?? 'This payment session has expired and this link can no longer be used. Please ask the merchant for a new payment link.'); return; }
    if (!accepted) { setMessage(t.acceptRequired); return; }
    setSubmitting(true);
    setMessage(null);
    setPhase('processing');
    onStep(3);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}${window.location.pathname}?returned=1` },
      redirect: 'if_required',
    });
    if (error) {
      const err = error as { message?: string; decline_code?: string; code?: string };
      setDecline({ msg: err.message ?? 'The payment could not be completed.', code: err.decline_code ?? err.code ?? null });
      setPhase('declined');
      setSubmitting(false);
      return;
    }
    // Success hands off to the server-authoritative status screen (the Result
    // step keeps animating there until a verified event confirms it).
    onSubmitted();
  }

  function backToForm(toStep: 1 | 2): void {
    setPhase(null); setDecline(null); setSubmitting(false); onStep(toStep);
  }

  return (
    <>
      {phase ? (
        <ResultView
          phase={phase}
          amount={payment.amount}
          currency={payment.currency}
          merchant={merchant.name}
          merchantLogo={merchant.logo_data_uri}
          paymentId={payment.id}
          message={decline?.msg ?? null}
          declineCode={decline?.code ?? null}
          t={t}
          locale={locale}
          onRetry={() => backToForm(2)}
          onChangeMethod={() => backToForm(1)}
        />
      ) : null}
      {/* Form stays mounted (hidden during the Result) so its single Payment
          Element is never remounted. */}
      <form onSubmit={pay} style={{ display: phase ? 'none' : 'block' }}>
      <div className="sp-cols" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 34, alignItems: 'start' }}>
        <style>{`@media (max-width:820px){ .sp-cols{ grid-template-columns: 1fr !important; } }`}</style>

        <div style={{ display: 'grid', gap: 26 }}>
          {/* Payment request (both steps) */}
          <Panel title={x.payRequest ?? 'Payment request'}>
            {/* The merchant's own logo, so the customer sees who they are paying. */}
            {merchant.logo_data_uri ? (
              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${LINE}`, textAlign: 'center' }}>
                <img src={merchant.logo_data_uri} alt={merchant.name} style={{ height: 52, maxWidth: '100%', width: 'auto', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
              </div>
            ) : null}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
              <tbody>
                <ReqRow label={t.merchant} value={merchant.name} bold />
                {payment.merchant_reference ? <ReqRow label={x.invoiceNo ?? 'Invoice number'} value={payment.merchant_reference} mono /> : null}
                {payment.description ? <ReqRow label={t.description} value={payment.description} /> : null}
                <ReqRow label={t.total} value={money(payment.amount, payment.currency, locale)} mono amount />
              </tbody>
            </table>
          </Panel>

          {/* Step 1: choose + enter (Stripe element). Kept mounted on step 2. */}
          <div style={{ display: step === 1 ? 'block' : 'none' }}>
            <Panel title={t.chooseMethod}>
              <PaymentElement onReady={() => setReady(true)} options={{ layout: PAYMENT_ELEMENT_LAYOUT }} />
              <p style={{ marginTop: 14, marginBottom: 0, fontSize: 12.5, color: FAINT }}>{t.methodsNote}</p>
            </Panel>
            {methods && methods.unavailable.length > 0 ? (
              <div style={{ marginTop: 16 }}>
                <Panel title={t.unavailableTitle}>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: MUTED }}>
                    {methods.unavailable.map((m) => <li key={m.method} style={{ marginBottom: 6 }}>{m.detail}</li>)}
                  </ul>
                </Panel>
              </div>
            ) : null}
          </div>

          {/* Step 2: review */}
          {step === 2 ? (
            <Panel>
              <p style={{ margin: '0 0 14px', fontSize: 15, color: INK }}>{x.reviewLine ?? 'Please review and confirm your payment.'}</p>
              <div style={{ background: '#f7f8f9', border: `1px solid ${LINE}`, padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#5cb87f', marginTop: 6, flex: 'none' }} />
                <span style={{ fontSize: 13.5, color: INK }}>{x.holdCard ?? 'The amount is authorized right away; the processing fee is captured once your bank approves the charge.'}</span>
              </div>
            </Panel>
          ) : null}

          {message ? <div style={{ background: '#fff5f5', border: '1px solid #f0c9c9', color: '#a12a2a', padding: '12px 14px', fontSize: 14 }}>{message}</div> : null}
        </div>

        {/* Right: order summary + actions */}
        <div style={{ display: 'grid', gap: 16, position: 'sticky', top: 20 }}>
          <div style={{ background: '#fff', border: `1px solid ${LINE}`, boxShadow: 'rgba(38,49,58,0.05) 0 1px 2px' }}>
            <div style={{ padding: '13px 20px', borderBottom: `1px solid #e2e5e8`, fontSize: 12, letterSpacing: '0.09em', textTransform: 'uppercase', color: MUTED, background: '#f7f8f9' }}>{x.orderSummary ?? 'Order summary'}</div>
            <div style={{ padding: '18px 20px' }}>
              <SumRow label={x.sumSubtotal ?? 'Subtotal'} value={money(payment.amount, payment.currency, locale)} />
              <div style={{ borderTop: `2px solid ${INK}`, margin: '14px 0 0', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: INK }}>{x.totalDue ?? 'Total due'}</span>
                <span className="wc-num" style={{ fontSize: 24, fontWeight: 700, color: INK }}>{money(payment.amount, payment.currency, locale)}</span>
              </div>
            </div>
          </div>

          {expired ? (
            <div style={{ background: '#fff5f5', border: '1px solid #f0c9c9', color: '#a12a2a', padding: '12px 14px', fontSize: 13.5, lineHeight: 1.45 }}>
              {x.sessionExpiredNote ?? 'This payment session has expired and this link can no longer be used. Please ask the merchant for a new payment link.'}
            </div>
          ) : step === 1 ? (
            <button type="button" onClick={() => onStep(2)} disabled={!stripe || !ready} style={btn(btnPrimary, !stripe || !ready)}>
              {x.continueBtn ?? 'Continue'}
            </button>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.45, cursor: 'pointer' }}>
                <input type="checkbox" checked={accepted} onChange={(e) => { setAccepted(e.target.checked); if (e.target.checked) setMessage(null); }} style={{ marginTop: 2, flex: 'none', width: 16, height: 16, accentColor: NAVY }} />
                <span>
                  {t.acceptPre}{' '}<button type="button" onClick={(e) => { e.preventDefault(); onOpenLegal('terms'); }} style={linkBtn}>{t.terms}</button>{' '}{t.acceptMid}{' '}<button type="button" onClick={(e) => { e.preventDefault(); onOpenLegal('privacy'); }} style={linkBtn}>{t.privacy}</button>{t.acceptPost}
                </span>
              </label>
              <button type="submit" disabled={!stripe || submitting || !accepted} style={btn(btnPrimary, !stripe || submitting || !accepted)}>
                {submitting ? t.paying : `${t.payNow} ${money(payment.amount, payment.currency, locale)}`}
              </button>
              <button type="button" onClick={() => onStep(1)} disabled={submitting} style={btnGhost}>{x.changeMethod ?? 'Change method'}</button>
            </div>
          )}
          <p style={{ margin: 0, fontSize: 12, color: FAINT, lineHeight: 1.45 }}>{t.sellerNote}</p>
        </div>
      </div>
      </form>
    </>
  );
}

const btnPrimary: React.CSSProperties = { width: '100%', background: NAVY, color: '#fff', border: 'none', padding: '14px 20px', fontSize: 15.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const btnGhost: React.CSSProperties = { width: '100%', background: '#fff', color: INK, border: `1px solid ${LINE}`, padding: '13px 20px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, color: '#2f6188', textDecoration: 'underline', font: 'inherit', cursor: 'pointer' };
/** A button that visibly greys out (not just `disabled`) when its guard is unmet. */
function btn(base: React.CSSProperties, disabled: boolean): React.CSSProperties {
  return disabled ? { ...base, opacity: 0.45, cursor: 'not-allowed' } : base;
}

function ReqRow({ label, value, bold, mono, amount }: { label: string; value: string; bold?: boolean; mono?: boolean; amount?: boolean }) {
  return (
    <tr>
      <td style={{ padding: amount ? '15px 0 3px' : '8px 0', color: MUTED, width: 190, verticalAlign: 'top', borderTop: amount ? `2px solid ${INK}` : undefined, fontWeight: amount ? 700 : 400 }}>{label}</td>
      <td className={mono ? 'wc-num' : undefined} style={{ padding: amount ? '15px 0 3px' : '8px 0', color: INK, fontWeight: bold || amount ? 700 : 500, borderTop: amount ? `2px solid ${INK}` : undefined, lineHeight: 1.45 }}>{value}</td>
    </tr>
  );
}

function SumRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 14.5, color: INK, padding: '4px 0' }}>
      <span style={{ color: MUTED }}>{label}</span>
      <span className="wc-num">{value}</span>
    </div>
  );
}
